<template>
    <div class="grid flex flex-column justify-content-center align-items-center  w-full ">
        <CustomBreadcrumb class="flex align-self-start p-4 "
            :items="[{ label: 'Profile Settings' }, { label: 'Qualifications' }]" />
        <span class="H6-Medium block text-center  mt-6 ">Edit your teaching information</span>
        <Form class="md:col-11 md:p-5 mt-4 pl-4" style="min-width:0; max-width:100%; box-sizing:border-box;">
            <h4 class="Paragraph-Medium text-left">Qualifications</h4>

            <div class="grid">
                <div class="flex flex-column col-12 justify-content-start gap-2 pt-4">
                    <!-- Highest Education entries -->
                    <div class="grid align-items-center mb-3" v-for="(edu, index) in highestEducations" :key="'edu-' + index">
                        <div class="col-12 md:col-3">
                            <label v-if="index === 0" class="Label" :for="'highestEdu-' + index">
                                Higher education qualifications:
                            </label>
                        </div>
                        <div class="col-12 md:col-9 flex gap-2 overflow-hidden">
                            <Button size="small" icon="pi pi-times" severity="danger" aria-label="Remove"
                                variant="outlined" @click="removeHighestEducation(index)" v-if="index > 0"
                                class="flex-shrink-0" />
                            <InputText v-model="edu.education" size="small" :id="'highestEdu-' + index"
                                variant="filled" class="flex-1" style="min-width:0"
                                placeholder="Enter highest education qualification here" />
                            <FileUpload mode="basic" @select="uploadHighestCertificate($event, index)"
                                :chooseLabel="edu.certificationURL ? 'Uploaded' : 'Upload'"
                                :chooseIcon="edu.certificationURL ? 'pi pi-check' : 'pi pi-upload'"
                                customUpload auto severity="secondary"
                                class="teal-button-transparent mobile-icon-btn flex-shrink-0" />
                        </div>
                    </div>

                </div>

                <div class="col-12">
                    <div class="flex justify-content-start py-2">
                        <Button label="Add" class="teal-button" @click="addHighestEducation" />
                    </div>
                </div>

                <div class="flex flex-column col-12 justify-content-start gap-2">
                    <!-- Teaching qualifications -->
                    <div class="grid align-items-center mb-3" v-for="(qualification, index) in qualifications"
                        :key="'qual-' + index">
                        <div class="col-12 md:col-3">
                            <label v-if="index === 0" class="Label" :for="'teachingEdu-' + index">
                                Teaching qualifications (if any):
                            </label>
                        </div>
                        <div class="col-12 md:col-9 flex gap-2 overflow-hidden">
                            <Button size="small" icon="pi pi-times" severity="danger" aria-label="Cancel" variant="outlined"
                                @click="removeQualification(index)" v-if="index > 0" class="flex-shrink-0" />
                            <InputText v-model="qualification.qualification" size="small" :id="'teachingEdu-' + index"
                                variant="filled" class="flex-1" style="min-width:0"
                                placeholder="Enter teaching qualification here" />
                            <FileUpload mode="basic" @select="uploadTeaching($event, index)"
                                :chooseLabel="qualification.certificateURL ? 'Uploaded' : 'Upload'"
                                :chooseIcon="qualification.certificateURL ? 'pi pi-check' : 'pi pi-upload'" customUpload
                                auto severity="secondary"
                                class="teal-button-transparent mobile-icon-btn flex-shrink-0" />
                        </div>
                    </div>
                </div>

                <div class="col-12">
                    <div class="flex justify-content-start py-2">
                        <Button label="Add" class="teal-button" @click="addQualification" />
                    </div>
                </div>
                <Divider class="w-full" />

                <div class="flex flex-column col-12 justify-content-start gap-2 pt-4">
                    <!-- Notable Achievements -->
                    <div class="grid align-items-center mb-3" v-for="(achievement, index) in achievements" :key="index">
                        <div class="col-12 md:col-3">
                            <label v-if="index === 0" class="Label" :for="'achievement-' + index">
                                Notable Achievements & Certifications:
                            </label>
                        </div>
                        <div class="col-12 md:col-9 flex gap-2">
                            <Button size="small" icon="pi pi-times" severity="danger" aria-label="Remove"
                                variant="outlined" @click="removeAchievement(index)" v-if="index > 0"
                                class="flex-shrink-0" />
                            <InputText v-model="achievement.achievement" size="small" :id="'achievement-' + index"
                                variant="filled" class="flex-1" style="min-width:0"
                                placeholder="Enter achievement or certification here" />
                            <div class="relative flex-shrink-0">
                                <FileUpload mode="basic" @select="uploadAchievement($event, index)"
                                    :chooseLabel="achievement.uploadURL ? 'Uploaded' : 'Upload'"
                                    :chooseIcon="achievement.uploadURL ? 'pi pi-check' : 'pi pi-upload'"
                                    customUpload auto severity="secondary"
                                    class="teal-button-transparent mobile-icon-btn" />
                                <small v-if="!achievement.uploadURL" class="text-500 absolute w-full text-center" style="font-size: 0.7rem; line-height: 1; top: 100%; margin-top: 2px;">optional</small>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="col-12">
                    <div class="flex justify-content-start py-2">
                        <Button label="Add" class="teal-button" @click="addAchievement" />
                    </div>
                </div>
            </div>

            <div class="col-12 col-6 flex justify-content-between p-0">
                <div class=" align-items-center gap-3 ">
                    <Button label="Save" class="teal-button mt-6" @click="Next"
                        :disabled="!isFormValid || !isFormDirty" />
                </div>
            </div>

        </Form>
    </div>
</template>

<script setup>
import { ref, defineEmits, defineProps, watch, computed } from "vue";
import { Form } from "@primevue/forms";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Divider from "primevue/divider";
import FileUpload from 'primevue/fileupload';
import { uploadCertificates, uploadAchievements } from "@/api/storage";
import { notifyError, notifyInfo } from "@/utils/notification";
import CustomBreadcrumb from '@/components/CustomBreadcrumb.vue';

const emit = defineEmits(["submit-experiance-info", "previous-step"]);
const props = defineProps({
    profile: {
        type: Object,
        required: true
    }
});
const qualifications = ref([{ qualification: "", certificateURL: "" }]);
const highestEducations = ref([{ education: "", certificationURL: "" }]);
const achievements = ref([{ achievement: "", uploadURL: "" }]);
const originalState = ref(null);

watch(
    () => props.profile,
    (newData) => {
        if (newData) {
            highestEducations.value = newData.highestEducation?.length
                ? newData.highestEducation.map(e => ({
                    education: e.education || '',
                    certificationURL: e.certificationURL || ''
                }))
                : [{ education: "", certificationURL: "" }];

            qualifications.value = newData.teachingQualifications?.length
                ? newData.teachingQualifications.map(q => ({
                    qualification: q.qualification || '',
                    certificateURL: q.certificateURL || ''
                }))
                : [{ qualification: "", certificateURL: "" }];

            achievements.value = newData.notableAchievements?.length
                ? newData.notableAchievements.map(a => ({
                    achievement: a.achievement || '',
                    uploadURL: a.uploadURL || ''
                }))
                : [{ achievement: "", uploadURL: "" }];

            originalState.value = {
                highestEducations: JSON.parse(JSON.stringify(highestEducations.value)),
                qualifications: JSON.parse(JSON.stringify(qualifications.value)),
                achievements: JSON.parse(JSON.stringify(achievements.value)),
            };
        }
    },
    { immediate: true }
);

const addHighestEducation = () => {
    highestEducations.value.push({ education: "", certificationURL: "" });
};

const removeHighestEducation = (index) => {
    highestEducations.value.splice(index, 1);
};

const uploadHighestCertificate = async (event, index) => {
    const file = event.files[0];
    highestEducations.value[index].certificationURL = await uploadFiles(file);
    notifyInfo('File uploaded successfully.');
};

const addQualification = () => {
    qualifications.value.push({ qualification: "", certificateURL: "" });
};

const uploadTeaching = async (event, index) => {
    const file = event.files[0];
    qualifications.value[index].certificateURL = await uploadFiles(file);
    notifyInfo('File uploaded successfully.');
};

const removeQualification = (index) => {
    qualifications.value.splice(index, 1);
};

const addAchievement = () => {
    achievements.value.push({ achievement: "", uploadURL: "" });
};

const removeAchievement = (index) => {
    achievements.value.splice(index, 1);
};

const uploadAchievement = async (event, index) => {
    const file = event.files[0];
    const formData = new FormData();
    formData.append('file', file);
    try {
        const response = await uploadAchievements(formData);
        achievements.value[index].uploadURL = response.data.url;
        notifyInfo('File uploaded successfully.');
    } catch {
        notifyError('File upload failed. Please try again.');
    }
};

const uploadFiles = async (file) => {
    const formData = new FormData();
    formData.append('file', file);

    try {
        const response = await uploadCertificates(formData);
        return response.data.url;
    } catch (error) {
        notifyError('File upload failed. Please try again.');
        return null;
    }
};

const Next = () => {
    const payload = {
        highestEducation: highestEducations.value
            .filter(e => e.education)
            .map(e => e.certificationURL ? { education: e.education, certificationURL: e.certificationURL } : { education: e.education }),
        teachingQualifications: qualifications.value.map(qual => ({
            qualification: qual.qualification,
            certificateURL: qual.certificateURL
        })),
        notableAchievements: achievements.value
            .filter(a => a.achievement)
            .map(a => a.uploadURL ? { achievement: a.achievement, uploadURL: a.uploadURL } : { achievement: a.achievement })
    }
    emit("update-profile", payload);
};
const isFormValid = computed(() => {
    const hasEducation = highestEducations.value.some(e => e.education?.trim());
    const educationCertsValid = highestEducations.value.every(
        e => !e.education?.trim() || e.certificationURL
    );
    const qualsValid = qualifications.value.every(
        q => (q.qualification?.trim() && q.certificateURL) || !q.qualification?.trim()
    );
    return hasEducation && educationCertsValid && qualsValid;
});

const isFormDirty = computed(() => {
    if (!originalState.value) {
        return false;
    }

    return (
        JSON.stringify(highestEducations.value) !== JSON.stringify(originalState.value.highestEducations) ||
        JSON.stringify(qualifications.value) !== JSON.stringify(originalState.value.qualifications) ||
        JSON.stringify(achievements.value) !== JSON.stringify(originalState.value.achievements)
    );
});

</script>

<style scoped>
.form {
    width: 100%;
    min-width: 60vw;
}

@media (max-width: 768px) {
    .form {
        min-width: 0;
        max-width: 100%;
        overflow-x: hidden;
    }
}
</style>

<style>
@media (max-width: 768px) {
    .mobile-icon-btn .p-button-label {
        display: none !important;
    }
    .mobile-icon-btn .p-button {
        min-width: 0 !important;
        padding: 0.375rem !important;
    }
    .mobile-icon-btn .p-button-icon {
        margin: 0 !important;
    }
}
</style>
